import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, Table, TableHead, TableRow, TableCell, TableBody, Tabs, Tab, Button, LinearProgress, Stack, Typography, Chip } from '@mui/material';
import { printReport, textCol, currencyCol } from '../../utils/pdfPrint';
import apiService from '../../services/apiServices';
import PageHeader from '../common/PageHeader';
import SendReportEmailDialog from '../common/SendReportEmailDialog';
import { Link } from 'react-router-dom';
import { ExportMenu, ReportPage } from './ReportKit';

export default function HRSpendReport() {
    const [lists, setLists] = useState([]);
    const [salariesByList, setSalariesByList] = useState({});
    const [workers, setWorkers] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [tab, setTab] = useState(0);
    const [emailOpen, setEmailOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const { t } = useTranslation();

    useEffect(() => {
        Promise.all([
            apiService().get('salariesmonthlylists'),
            apiService().get('workers'),
            apiService().get('departments'),
        ]).then(([lr, wr, dr]) => {
            if (lr?.success) setLists(lr.data || []);
            if (wr?.success) setWorkers(wr.data || []);
            if (dr?.success) setDepartments(dr.data || []);
        }).catch(() => {}).finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        async function load() {
            const out = {};
            await Promise.all((lists || []).map(async (l) => {
                const res = await apiService().query('getSalariesByListId', `id=${l.id}`);
                out[l.id] = res?.success ? (res.data || []) : [];
            }));
            setSalariesByList(out);
        }
        if (lists.length) load();
    }, [lists]);

    const deptMap = useMemo(() => {
        const map = new Map();
        departments.forEach((d) => {
            map.set(String(d.Id || d.id || ''), d.Name || d.name || String(d.Id || d.id));
        });
        return map;
    }, [departments]);

    const getDeptName = (workerId) => {
        const w = (workers || []).find((x) => String(x.Id || x.id) === String(workerId));
        if (!w) return t('report.noDept');
        const deptId = String(w.Department || w.DepartmentId || '');
        return deptMap.get(deptId) || w.DepartmentName || deptId || t('report.noDept');
    };

    const fmt = (n) => new Intl.NumberFormat('sq-AL', { style: 'currency', currency: 'EUR' }).format(n || 0);

    const computeRow = (rows) => {
        let net = 0, taxes = 0, contrib = 0;
        for (const r of rows) {
            const salary = Number(r.Salary || r.GrossSalary || 0);
            net += Number(r.neto || r.NetSalary || salary * 0.85 || 0);
            taxes += Number(r.taxes || r.Tax || salary * 0.05 || 0);
            contrib += Number(r.contributions || r.Contribution || salary * 0.1 || 0);
        }
        return { net: +net.toFixed(2), taxes: +taxes.toFixed(2), contrib: +contrib.toFixed(2), total: +(net + taxes + contrib).toFixed(2) };
    };

    const byMonth = useMemo(() => {
        return (lists || []).map((l) => {
            const rows = salariesByList[l.id] || [];
            const { net, taxes, contrib, total } = computeRow(rows);
            return { key: `${l.month}/${l.year}`, month: l.month, year: l.year, net, taxes, contrib, total };
        }).sort((a, b) => (a.year - b.year) || (a.month - b.month));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lists, salariesByList]);

    const byDept = useMemo(() => {
        const agg = {};
        for (const l of (lists || [])) {
            const rows = salariesByList[l.id] || [];
            for (const r of rows) {
                const dept = getDeptName(r.WorkerId || r.Employee || r.Id);
                if (!agg[dept]) agg[dept] = { dept, net: 0, taxes: 0, contrib: 0 };
                agg[dept].net += Number(r.neto || r.NetSalary || 0);
                agg[dept].taxes += Number(r.taxes || r.Tax || 0);
                agg[dept].contrib += Number(r.contributions || r.Contribution || 0);
            }
        }
        return Object.values(agg)
            .map((x) => ({ ...x, total: x.net + x.taxes + x.contrib }))
            .sort((a, b) => b.total - a.total);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lists, salariesByList, workers, departments]);

    const grandTotal = useMemo(() => byMonth.reduce((acc, r) => acc + r.total, 0), [byMonth]);

    function exportMonthCsv() {
        const headers = [t('report.month'), t('report.net'), t('report.tax'), t('report.contributions'), t('report.total')];
        const lines = [headers.join(',')];
        byMonth.forEach((r) => lines.push([r.key, r.net, r.taxes, r.contrib, r.total].map((x) => `"${x}"`).join(',')));
        const blob = new Blob([lines.join('\r\n')], { type: 'text/csv;charset=utf-8;' });
        const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = 'hr_spend_monthly.csv'; a.click();
    }

    function exportMonthPdf() {
        printReport({
            title: t('report.monthlyCostByMonth'),
            orientation: 'portrait',
            columns: [
                textCol(t('report.month'), 'key', '20%'),
                currencyCol(t('report.net'), 'net', '20%'),
                currencyCol(t('report.tax'), 'taxes', '20%'),
                currencyCol(t('report.contributions'), 'contrib', '20%'),
                currencyCol(t('report.total'), 'total', '20%'),
            ],
            rows: byMonth,
            totals: { net: byMonth.reduce((a,r)=>a+r.net,0), taxes: byMonth.reduce((a,r)=>a+r.taxes,0), contrib: byMonth.reduce((a,r)=>a+r.contrib,0), total: grandTotal },
            badge: { label: `${byMonth.length} ${t('report.periods')}` },
        });
    }

    function exportDeptPdf() {
        printReport({
            title: t('report.monthlyCostByDept'),
            orientation: 'portrait',
            columns: [
                textCol(t('report.department'), 'dept', '30%'),
                currencyCol(t('report.net'), 'net', '17%'),
                currencyCol(t('report.tax'), 'taxes', '17%'),
                currencyCol(t('report.contributions'), 'contrib', '18%'),
                currencyCol(t('report.total'), 'total', '18%'),
            ],
            rows: byDept,
            totals: { net: byDept.reduce((a,r)=>a+r.net,0), taxes: byDept.reduce((a,r)=>a+r.taxes,0), contrib: byDept.reduce((a,r)=>a+r.contrib,0), total: grandTotal },
            badge: { label: `${byDept.length} ${t('report.departments')}` },
        });
    }

    function exportDeptCsv() {
        const headers = [t('report.department'), t('report.net'), t('report.tax'), t('report.contributions'), t('report.total')];
        const lines = [headers.join(',')];
        byDept.forEach((r) => lines.push([r.dept, r.net, r.taxes, r.contrib, r.total].map((x) => `"${x}"`).join(',')));
        const blob = new Blob([lines.join('\r\n')], { type: 'text/csv;charset=utf-8;' });
        const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = 'hr_spend_dept.csv'; a.click();
    }

    return (
        <ReportPage>
            <PageHeader
                title={t('reports.laborCost')}
                actions={<Button component={Link} to="/home/reports" variant="outlined">{t('report.backLabel')}</Button>}
            />
            {loading && <LinearProgress sx={{ mb: 2 }} />}

            <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', flex: 1 }}>
                    <CardContent sx={{ py: 1.5 }}>
                        <Typography variant="caption" color="text.secondary">{t('report.grandTotal')}</Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>{fmt(grandTotal)}</Typography>
                    </CardContent>
                </Card>
                <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', flex: 1 }}>
                    <CardContent sx={{ py: 1.5 }}>
                        <Typography variant="caption" color="text.secondary">{t('report.periods')}</Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>{byMonth.length} {t('report.months')}</Typography>
                    </CardContent>
                </Card>
                <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', flex: 1 }}>
                    <CardContent sx={{ py: 1.5 }}>
                        <Typography variant="caption" color="text.secondary">{t('report.departments')}</Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>{byDept.length}</Typography>
                    </CardContent>
                </Card>
            </Stack>

            <Tabs
                value={tab}
                onChange={(_, v) => setTab(v)}
                sx={{ mb: 2, borderBottom: '1px solid', borderColor: 'divider', '& .MuiTab-root': { textTransform: 'none', fontWeight: 600 } }}
            >
                <Tab label={t('report.monthlyCostByMonth')} />
                <Tab label={t('report.monthlyCostByDept')} />
            </Tabs>

            {tab === 0 && (
                <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
                    <CardContent>
                        <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ mb: 1.5 }}>
                            <ExportMenu onCsv={exportMonthCsv} onPdf={exportMonthPdf} onEmail={() => setEmailOpen(true)} disabled={byMonth.length === 0} />
                        </Stack>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 700 }}>{t('report.month')}</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700 }}>{t('report.net')}</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700 }}>{t('report.tax')}</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700 }}>{t('report.contributions')}</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700 }}>{t('report.total')}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {byMonth.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                                            {t('report.noPayrollData')}
                                        </TableCell>
                                    </TableRow>
                                )}
                                {byMonth.map((r) => (
                                    <TableRow key={r.key} hover>
                                        <TableCell sx={{ fontWeight: 600 }}>{r.key}</TableCell>
                                        <TableCell align="right">{fmt(r.net)}</TableCell>
                                        <TableCell align="right">{fmt(r.taxes)}</TableCell>
                                        <TableCell align="right">{fmt(r.contrib)}</TableCell>
                                        <TableCell align="right">
                                            <Chip label={fmt(r.total)} size="small" color="primary" variant="outlined" />
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {byMonth.length > 0 && (
                                    <TableRow sx={{ bgcolor: 'action.hover' }}>
                                        <TableCell sx={{ fontWeight: 700 }}>{t('report.total')}</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 700 }}>{fmt(byMonth.reduce((a, r) => a + r.net, 0))}</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 700 }}>{fmt(byMonth.reduce((a, r) => a + r.taxes, 0))}</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 700 }}>{fmt(byMonth.reduce((a, r) => a + r.contrib, 0))}</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 700 }}>{fmt(grandTotal)}</TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </CardContent>
                </Card>
            )}

            {tab === 1 && (
                <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
                    <CardContent>
                        <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ mb: 1.5 }}>
                            <ExportMenu onCsv={exportDeptCsv} onPdf={exportDeptPdf} onEmail={() => setEmailOpen(true)} disabled={byDept.length === 0} />
                        </Stack>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 700 }}>{t('report.department')}</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700 }}>{t('report.net')}</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700 }}>{t('report.tax')}</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700 }}>{t('report.contributions')}</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700 }}>{t('report.total')}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {byDept.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                                            {t('report.noData')}
                                        </TableCell>
                                    </TableRow>
                                )}
                                {byDept.map((r) => (
                                    <TableRow key={r.dept} hover>
                                        <TableCell sx={{ fontWeight: 600 }}>{r.dept}</TableCell>
                                        <TableCell align="right">{fmt(r.net)}</TableCell>
                                        <TableCell align="right">{fmt(r.taxes)}</TableCell>
                                        <TableCell align="right">{fmt(r.contrib)}</TableCell>
                                        <TableCell align="right">
                                            <Chip label={fmt(r.total)} size="small" color="secondary" variant="outlined" />
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </CardContent>
                </Card>
            )}
        <SendReportEmailDialog
            open={emailOpen}
            onClose={() => setEmailOpen(false)}
            reportTitle={tab === 0 ? t('report.monthlyCostByMonth') : t('report.monthlyCostByDept')}
            subtitle={`${t('report.total')}: ${tab === 0 ? byMonth.length : byDept.length} ${t('report.records')}`}
            columns={tab === 0 ? [
                { label: t('report.month'), key: 'key', align: 'left' },
                { label: t('report.net'), key: 'net', align: 'right', format: 'currency' },
                { label: t('report.tax'), key: 'taxes', align: 'right', format: 'currency' },
                { label: t('report.contributions'), key: 'contrib', align: 'right', format: 'currency' },
                { label: t('report.total'), key: 'total', align: 'right', format: 'currency' },
            ] : [
                { label: t('report.department'), key: 'dept', align: 'left' },
                { label: t('report.net'), key: 'net', align: 'right', format: 'currency' },
                { label: t('report.tax'), key: 'taxes', align: 'right', format: 'currency' },
                { label: t('report.contributions'), key: 'contrib', align: 'right', format: 'currency' },
                { label: t('report.total'), key: 'total', align: 'right', format: 'currency' },
            ]}
            rows={tab === 0 ? byMonth : byDept}
            totals={tab === 0
                ? { net: byMonth.reduce((a, r) => a + r.net, 0), taxes: byMonth.reduce((a, r) => a + r.taxes, 0), contrib: byMonth.reduce((a, r) => a + r.contrib, 0), total: grandTotal }
                : { net: byDept.reduce((a, r) => a + r.net, 0), taxes: byDept.reduce((a, r) => a + r.taxes, 0), contrib: byDept.reduce((a, r) => a + r.contrib, 0), total: byDept.reduce((a, r) => a + r.total, 0) }
            }
            workers={workers}
        />
        </ReportPage>
    );
}
